/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-restricted-syntax */
import {
  FullStyle as Type,
  Cursor,
  Position,
  TextAlign,
  VerticalAlign,
  BackgroundStyle,
  GradientLayer,
  BorderStyle,
  FlexStyle,
  Display,
  VisibilityType,
} from '../types/base';

import {
  isValidSvg,
  getSizesBasedOnScreen,
  ScreenResolutions,
} from '../utils/base';

import {
  replaceKeyWithCssVar,
  resolveSpacingMargin,
  resolveSpacingPadding,
} from './base';

import {
  easingToCss,
  isIdentityAnimation,
} from './animation';

// 'init' (TNBEWEB-1960) is the on-load entrance state — applied via the
// active_state merge during the first-mount entrance phase, NOT as a `&:init`
// pseudo-class (which would never match). Block it like the runtime states.
const blockList = ['loading', 'success', 'error', 'disabled', 'init'];

const breakpointsOrder: ScreenResolutions[] = ['2k', '4k', 'laptopL', 'laptop', 'tablet', 'mobileL', 'mobileM', 'mobileS']; 

const getScrollbarWidth = (scroll: { type?: string } | undefined): string | undefined => {
  switch (scroll?.type) {
    case 'hidden':
    case 'visible':
    case 'auto':
    case 'scroll':
      return 'auto';
    case 'none':
    case 'transparent':
      return 'none';
    default:
      return undefined;
  }
};


function getOverflow(scroll, font, display, flex) {
  const scrollType = scroll?.type === 'transparent' ? 'none' : scroll?.type;

  if (font?.text_overflow === 'ellipsis' && !scrollType) {
    return {
      overflow: 'hidden',
    };
  } else if (!scrollType) {
    return {};
  }

  const overflow = scroll?.direction === 'both' ? (scrollType === 'none' ? 'auto' : (scrollType || 'auto')) : font?.text_overflow === 'ellipsis' ? 'hidden' : undefined;
  const overflowX = (scroll?.direction === 'x' || scroll?.direction === 'both') ? (scrollType || 'auto') : (display === 'grid')
    ? 'hidden'
    : (flex?.direction === 'row')
      ? (scrollType === 'none' ? 'auto' : (scrollType || 'hidden'))
      : 'hidden';
  const overflowY = (scroll?.direction === 'y' || scroll?.direction === 'both') ? (scrollType || 'auto') : (display === 'grid')
    ? (scrollType === 'none' ? 'auto' : (scrollType || 'hidden'))
    : (flex?.direction === 'column')
      ? (scrollType === 'none' ? 'auto' : (scrollType || 'hidden'))
      : 'hidden';

  return {
    overflow,
    overflowX,
    overflowY,
    overscrollBehavior: 'contain',
  };
}

function getBackgroundImage(input: string): string {
  if (isValidSvg(input)) {
    // If the input is a valid SVG, use it as a data URL
    return `url("data:image/svg+xml,${encodeURIComponent(input)}")`;
  } else {
    // If the input is not a valid SVG, assume it's a URL to an image
    return `url("${input}")`;
  }
}

// Helper type guards
const isBorderDirectional = (width: any): width is { type?: 'top' | 'right' | 'bottom' | 'left', value: string | number } => {
  return width?.type && ['top', 'right', 'bottom', 'left'].includes(width.type);
};

const isBorderCustom = (width: any): width is { type: 'custom', value: (string | number)[] } => {
  return width?.type === 'custom' && Array.isArray(width.value);
};

const normalizeStyleValue = <T>(value: T): T | string => {
  if (value === null || value === undefined || value === '') return value;
  if (typeof value === 'number') return `${value}px`;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^-?\d*\.?\d+$/.test(trimmed)) return `${trimmed}px`;
  }
  return value;
};
const generateGapStyles = (flex: FlexStyle | null | undefined): Record<string, string | number> => {
  if (!flex?.gap) return {};
  
  // Handle single number or string value
  if (typeof flex.gap === 'number') {
    return { gap: `${flex.gap}px` };
  }

  if (typeof flex.gap === 'string') {
    // Split by space or comma and trim whitespace
    const values = flex.gap.split(/[\s,]+/).filter(Boolean).map(val => val.trim());
    
    // Process each value to ensure proper format
    const processedValues = values.map(val => {
      // If it's a pure number
      if (/^\d+$/.test(val)) {
        return `${val}px`;
      }
      // If it already has units (px, em, rem, etc.)
      if (/^\d+(?:px|em|rem|vh|vw|%)$/.test(val)) {
        return val;
      }
      // Invalid format
      return val;
    });

    // Return formatted gap value based on number of values
    if (processedValues.length === 1) {
      return { gap: processedValues[0] };
    }
    if (processedValues.length === 2) {
      return { gap: processedValues.join(' ') };
    }
    // If more than 2 values, only use the first two
    return { gap: `${processedValues[0]} ${processedValues[1]}` };
  }

  return {};
};
// Modified border width generation
const generateBorderWidth = (border: BorderStyle) => {
  if (!border?.width) return {};

  if (typeof border.width === 'string' || typeof border.width === 'number') {
    return { borderWidth: normalizeStyleValue(border.width) };
  }

  if (border.width === null) {
    return { borderWidth: 0 };
  }

  if (isBorderDirectional(border.width)) {
    const { type, value } = border.width;
    return {
      borderWidth: 0,
      [`border${type?.charAt(0).toUpperCase()}${type?.slice(1)}Width`]: normalizeStyleValue(value),
    };
  }

  if (isBorderCustom(border.width)) {
    return {
      borderTopWidth: normalizeStyleValue(border.width.value[0]),
      borderRightWidth: normalizeStyleValue(border.width.value[1]),
      borderBottomWidth: normalizeStyleValue(border.width.value[2]),
      borderLeftWidth: normalizeStyleValue(border.width.value[3]),
    };
  }

  return {};
};

const generateBorderRadius = (border: BorderStyle) => {
  if (!border?.radius) return {};

  if (typeof border.radius === 'string' || typeof border.radius === 'number') {
    return { borderRadius: normalizeStyleValue(border.radius) };
  }

  if (border.radius === null) {
    return { borderRadius: 0 };
  }

  if (isBorderCustom(border.radius)) {
    return {
      borderTopLeftRadius: normalizeStyleValue(border.radius.value[0]),
      borderTopRightRadius: normalizeStyleValue(border.radius.value[1]),
      borderBottomRightRadius: normalizeStyleValue(border.radius.value[2]),
      borderBottomLeftRadius: normalizeStyleValue(border.radius.value[3]),
    };
  }

  return {};
}

const parseBackgroundSize = (
  url: string,
): { url: string, backgroundSize: string | null, rotate: number | null } => {
  try {
    const baseUrl = window.location.origin; // Base URL for relative URLs
    const urlObj = new URL(url, baseUrl);
    const backgroundSize = urlObj.searchParams.get('backgroundSize');
    const rotateRaw = urlObj.searchParams.get('rotate');
    if (backgroundSize !== null) {
      urlObj.searchParams.delete('backgroundSize');
    }
    if (rotateRaw !== null) {
      urlObj.searchParams.delete('rotate');
    }
    const resultUrl = url.startsWith('http') ? urlObj.toString() : urlObj.pathname + urlObj.search;
    const rotate = rotateRaw !== null && !Number.isNaN(Number(rotateRaw)) ? Number(rotateRaw) : null;
    return { url: resultUrl, backgroundSize, rotate };
  } catch (e) {
    return { url, backgroundSize: null, rotate: null };
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filterUndefinedProperties = (obj: { [key: string]: any }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filteredObj: { [key: string]: any } = {}; // Add index signature
  const validNumberRegex = /^-?\d+(\.\d+)?$/; // Regular expression to match valid numbers

  for (const key in obj) {
    if (obj[key] !== undefined) {
      let value = obj[key];
      if (typeof value === 'string' && validNumberRegex.test(value)) {
        value = parseFloat(value);
      }
      filteredObj[key] = replaceKeyWithCssVar(value);
    }
  }
  return filteredObj;
};

// Multiply a gradient layer's global opacity into every stop's alpha (non-destructive to the
// stored stops; CSS `background-image` has no per-layer opacity slot). Picker-emitted stops are
// always rgba; rgb stops are treated as alpha 1; non-rgb tokens (rare) are left untouched.
const applyGradientOpacity = (gradient: string, opacity?: number | null): string => {
  if (opacity == null || opacity >= 1) return gradient;
  const k = Math.max(0, Math.min(1, opacity));
  return gradient.replace(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/gi,
    (_m, r, g, b, a) => `rgba(${r}, ${g}, ${b}, ${+((a === undefined ? 1 : parseFloat(a)) * k).toFixed(3)})`,
  );
};

/**
 * TNBEWEB-1869 — every exit path must emit the SAME key set.
 *
 * States are emitted as their own CSS rules (`&:hover`, …), so a property this
 * function omits is simply not declared and the BASE rule keeps applying. When
 * a state overrode an image with a single colour it used to emit only
 * `background-color`, never clearing `background-image` — and `background-image`
 * paints ABOVE `background-color`, so the base image stayed visible on top of
 * the new colour. The reverse swap left the old colour bleeding through a
 * transparent PNG.
 *
 * `undefined` cannot fix that: `filterUndefinedProperties` DELETES those keys.
 * The cleared value has to be a real CSS one — `none` / `transparent`.
 */
const CLEARED_BACKGROUND = {
  backgroundColor: 'transparent',
  backgroundImage: 'none',
  backgroundPosition: undefined,
  backgroundSize: undefined,
  backgroundRepeat: undefined,
};

function getBackgroundProperties(shapes?: BackgroundStyle[] | null) {
  // ABSENT `background` means "inherit" — emit nothing at all so the broader
  // scope's rule survives. This is what makes Fill inheritance work, and it is
  // deliberately NOT the same as an empty array below.
  if (shapes == null || !Array.isArray(shapes)) {
    return {};
  }

  // An EMPTY array is an explicit "this scope has no fill", so it must clear.
  if (shapes.length === 0) {
    return { ...CLEARED_BACKGROUND };
  }

  const backgroundImages: string[] = [];
  const backgroundPositions: string[] = [];
  const backgroundSizes: string[] = [];
  const backgroundRepeats: string[] = [];
  const isSingleColor = shapes.length === 1 && shapes[0].type === 'color';

  if (isSingleColor) {
    return {
      ...CLEARED_BACKGROUND,
      backgroundColor: shapes[0].value,
    };
  }

  shapes
    .filter((el) => !((el.type === 'image' || el.type === 'gradient') && el.opacity === 0))
    .forEach((shape) => {
      if (shape.value?.startsWith('linear-gradient') || shape.value?.startsWith('radial-gradient')) {
        backgroundImages.push(applyGradientOpacity(shape.value, (shape as GradientLayer).opacity));
        backgroundPositions.push('center');
        backgroundSizes.push('cover');
        backgroundRepeats.push('no-repeat');
      } else if (shape.value?.startsWith('http') || shape.value?.startsWith('/')) {
        backgroundImages.push(`url(${parseBackgroundSize(shape.value).url})`);
        backgroundPositions.push('center');
        backgroundSizes.push(parseBackgroundSize(shape.value).backgroundSize || 'contain');
        backgroundRepeats.push('no-repeat');
      } else {
        const gradientValue = `linear-gradient(${shape.value}, ${shape.value})`;
        backgroundImages.push(gradientValue);
        backgroundPositions.push('center');
        backgroundSizes.push('cover');
        backgroundRepeats.push('no-repeat');
      }
    });

  const hasImages = backgroundImages.length > 0;

  // Still spread the cleared defaults: a multi-layer stack composes everything
  // into `background-image`, so it must explicitly reset the `background-color`
  // a broader scope's single-colour rule may have set.
  return {
    ...CLEARED_BACKGROUND,
    backgroundImage: hasImages ? backgroundImages.reverse().join(', ') : 'none',
    backgroundPosition: hasImages ? backgroundPositions.reverse().join(', ') : undefined,
    backgroundSize: hasImages ? backgroundSizes.reverse().join(', ') : undefined,
    backgroundRepeat: hasImages ? backgroundRepeats.reverse().join(', ') : undefined,
  };
}

const generateComponentStyles = (
  style: Type,
  baseWidth?: number,
  skipStates?: boolean,
  parent_display?: string,
  parentVisibility?: VisibilityType,
  parent_flex_direction?: string,
) => {
  if (!style) return null;
  const {
    font,
    align,
    height,
    width,
    max_width,
    min_width,
    max_height,
    min_height,
    margin,
    padding,
    grid,
    border,
    position,
    z_index,
    user_select,
    scroll,
    cursor,
    breakpoints,
    states,
    display,
    flex,
    box,
    background,
    aspect_ratio,

    left,
    bottom,
    top,
    right,

    object,

    transform,
    transform_origin,
    backface_visibility,
    transform_style,
    placeholder,
    opacity,
    visibility: visibilityParam,
    animation,
    // cols,
    // rows,
  } = style;

  const isFlexObject = flex && typeof flex !== 'string';
  const isBorderObject = border && typeof border !== 'string';
  // const backgroundShape = background?.[background.length - 1];

  const isFillWidth = width === 'fill';
  const isFillHeight = height === 'fill';
  const parentIsFlex = parent_display === 'flex';
  const parentDirIsColumn = parent_flex_direction === 'column' || parent_flex_direction === 'column-reverse';
  const widthIsMainAxisFill = isFillWidth && parentIsFlex && !parentDirIsColumn;
  const heightIsMainAxisFill = isFillHeight && parentIsFlex && parentDirIsColumn;
  const widthIsCrossAxisFill = isFillWidth && parentIsFlex && parentDirIsColumn;
  const heightIsCrossAxisFill = isFillHeight && parentIsFlex && !parentDirIsColumn;
  // Flex main axis -> flex-grow; cross axis -> 100%; grid/block/unknown -> no-op (already fills naturally)
  const resolvedWidth = isFillWidth ? (widthIsCrossAxisFill ? '100%' : undefined) : width;
  const resolvedHeight = isFillHeight ? (heightIsCrossAxisFill ? '100%' : undefined) : height;
  const fillNeedsFlexGrow = widthIsMainAxisFill || heightIsMainAxisFill;
  const explicitFlexGrow = isFlexObject ? flex?.grow : undefined;
  const effectiveFlexGrow = explicitFlexGrow !== undefined
    ? explicitFlexGrow
    : (fillNeedsFlexGrow ? 1 : undefined);

  const placeholderStyles = filterUndefinedProperties({
    fontWeight: placeholder?.weight,
    fontStyle: placeholder?.style,
    fontSize: normalizeStyleValue(placeholder?.size),
    color: placeholder?.color,
    fontFamily: placeholder?.family,
    lineHeight: normalizeStyleValue(placeholder?.height),
    letterSpacing: normalizeStyleValue(placeholder?.letter_spacing),
    textDecoration: placeholder?.decoration,
    textTransform: placeholder?.transform,
  });

  const componentStyles = filterUndefinedProperties({
    textOverflow: font?.text_overflow,
    textTransform: font?.transform,
    whiteSpace: font?.text_overflow === 'ellipsis'
      ? 'nowrap'
      : font?.text_wrap === 'wrap'
        ? 'normal'
        : 'nowrap',
    fontSize: normalizeStyleValue(font?.size),
    fontWeight: font?.weight,
    lineHeight: normalizeStyleValue(font?.height),
    letterSpacing: normalizeStyleValue(font?.letter_spacing),
    color: font?.color,
    fontFamily: font?.family,
    fontStyle: font?.style,
    textDecoration: font?.decoration,
    textAlign: align?.horizontal as TextAlign,
    verticalAlign: align?.vertical as VerticalAlign,
    gridAutoFlow: grid?.auto?.flow,
    gridAutoColumns: grid?.auto?.columns,
    gridAutoRows: grid?.auto?.rows,
    // A `display: grid` text element has a single implicit `auto` column, which
    // sizes to the text's one-line max-content and overflows the box instead of
    // wrapping. When wrap is enabled, cap the column at the container so
    // `white-space: normal` has a finite width to wrap into. Gated on text_wrap
    // so default (overflow) and ellipsis behavior are untouched.
    gridTemplateColumns: (font?.text_wrap === 'wrap' && display === 'grid') ? 'minmax(0, 1fr)' : undefined,
    height: normalizeStyleValue(resolvedHeight),
    width: normalizeStyleValue(resolvedWidth),
    aspectRatio: aspect_ratio,
    maxWidth: normalizeStyleValue(max_width || ((typeof flex?.basis === 'number' && resolvedWidth) ? resolvedWidth : undefined)),
    minWidth: normalizeStyleValue(min_width || ((typeof flex?.basis === 'number' && resolvedWidth) ? resolvedWidth : undefined)),
    maxHeight: normalizeStyleValue(max_height),
    minHeight: normalizeStyleValue(min_height),
    ...resolveSpacingMargin(margin),
    ...resolveSpacingPadding(padding),
    display: (visibilityParam || parentVisibility) && (visibilityParam === 'hidden' || parentVisibility === 'hidden') ? 'none' : (parentVisibility && parentVisibility === 'visible') ? parent_display : (visibilityParam && visibilityParam === 'visible') ? display : display,
    alignItems: grid?.align_items || (isFlexObject ? flex.align_items : undefined),
    alignContent: (isFlexObject) ? flex.align_items : undefined,
    cursor: cursor as Cursor,
    gridColumnStart: grid?.x ? `${grid.x}` : undefined,
    gridColumnEnd: grid?.cols ? `span ${grid?.cols}` : undefined,
    gridRowStart: grid?.y ? `${grid.y}` : undefined,
    gridRowEnd: grid?.rows ? `span ${grid?.rows}` : undefined,
    justifyItems: grid?.justify_items,
    justifyContent: grid?.justify_content || ((isFlexObject) ? flex.justify_content : undefined),
    ...getBackgroundProperties(background),
    flexDirection: isFlexObject ? flex?.direction : undefined,
    flexShrink: isFlexObject ? flex?.shrink : undefined,
    flexGrow: effectiveFlexGrow,
    flexBasis: (effectiveFlexGrow && effectiveFlexGrow !== '0' && effectiveFlexGrow !== 0) ? 0 : 'auto',
    flexWrap: isFlexObject ? flex?.wrap : undefined,
    ...generateGapStyles(flex),
        // Modified
    ...(isBorderObject ? generateBorderWidth(border) : {}),
    ...(isBorderObject ? generateBorderRadius(border) : {}),
    borderStyle: isBorderObject ? border?.style : undefined,
    borderColor: isBorderObject ? border?.color : undefined,
    border: !isBorderObject ? border : undefined,
    position: position ? `${position as Position} !important` : undefined,
    zIndex: z_index,
    ...getOverflow(scroll, font, display, flex),
    scrollSnapType: scroll?.snap ? 'both mandatory' : undefined,
    boxShadow: box?.shadow ? box?.shadow : undefined,
    boxSizing: box?.sizing ? box?.sizing : 'border-box',
    objectFit: object?.fit,
    objectPosition: object?.position,
    userSelect: user_select,

    left: normalizeStyleValue(left),
    bottom: normalizeStyleValue(bottom),
    top: normalizeStyleValue(top),
    right: normalizeStyleValue(right),
    transform,
    transformOrigin: transform_origin,
    backfaceVisibility: backface_visibility,
    transformStyle: transform_style,
    opacity,
    scrollbarWidth: getScrollbarWidth(scroll),
  });

  // Express the protocol `animation` block as a CSS `transition` on the base
  // rule so state changes (:hover/:active emitted below as pseudo-classes)
  // animate over the protocol's duration/easing instead of snapping. A base-rule
  // transition drives BOTH enter and leave of a pseudo-state, and does not fire
  // on first paint. Because this function recurses for `states` and
  // `breakpoints`, a per-state/per-breakpoint `animation` override emits its own
  // transition inside that nested rule.
  if (animation && !isIdentityAnimation(animation)) {
    componentStyles.transition = `all ${animation.duration}ms ${easingToCss(animation.easing)} ${animation.delay}ms`;
  }

  if (breakpoints) {
    const visibilityMap: { [key: string]: VisibilityType } = {};
    const displayMap: { [key: string]: string } = {};

    Object.entries(breakpoints).forEach(([breakpoint, styles]) => {
      const visibility = (styles as Type).visibility;
      const display = (styles as Type).display;
      if (visibility) {
        visibilityMap[breakpoint] = visibility;
      }
      if (display) {
        displayMap[breakpoint] = display;
      }
    });

    // Order breakpoints by their resolved pixel width (widest first). Custom
    // resolution ids resolve to their persisted width via getSizesBasedOnScreen,
    // so they participate in this order; the old hardcoded `breakpointsOrder`
    // (preset names only) gave custom ids indexOf === -1 and excluded them from
    // the visibility/display inheritance cascade. The preset order is kept only
    // as a deterministic tiebreaker when two keys resolve to the same width.
    const widthOf = (key: string) => getSizesBasedOnScreen(key as ScreenResolutions);
    const orderedKeys = Object.keys(breakpoints).sort((a, b) => {
      const byWidth = widthOf(b) - widthOf(a);
      if (byWidth !== 0) return byWidth;
      return breakpointsOrder.indexOf(a as ScreenResolutions) - breakpointsOrder.indexOf(b as ScreenResolutions);
    });

    orderedKeys.forEach((breakpoint) => {
      const styles = breakpoints[breakpoint];
      const baseWidthForBreakpoint = widthOf(breakpoint);
      const bpIndex = orderedKeys.indexOf(breakpoint);

      // Inherit visibility/display from the nearest explicitly-set breakpoint,
      // walking from this breakpoint toward the base resolution width. The base
      // width is the pivot (was hardcoded to laptopL, which broke any screen
      // whose base resolution wasn't laptopL).
      let inheritedVisibility = visibilityParam;
      let inheritedDisplay = parent_display;
      const pivot = baseWidth ?? baseWidthForBreakpoint;
      if (baseWidthForBreakpoint >= pivot) {
        // Wider than (or equal to) base: walk down toward the base width.
        for (let i = bpIndex; i < orderedKeys.length; i++) {
          if (widthOf(orderedKeys[i]) < pivot) break;
          if (visibilityMap[orderedKeys[i]]) {
            inheritedVisibility = visibilityMap[orderedKeys[i]];
            inheritedDisplay = displayMap[orderedKeys[i]] || inheritedDisplay;
            break;
          }
        }
      } else {
        // Narrower than base: walk up toward the base width.
        for (let i = bpIndex; i >= 0; i--) {
          if (widthOf(orderedKeys[i]) > pivot) break;
          if (visibilityMap[orderedKeys[i]]) {
            inheritedVisibility = visibilityMap[orderedKeys[i]];
            inheritedDisplay = displayMap[orderedKeys[i]] || inheritedDisplay;
            break;
          }
        }
      }

      const generatedStyles = generateComponentStyles(styles as Type, undefined, skipStates, inheritedDisplay, inheritedVisibility, parent_flex_direction); // Call only once per valid breakpoint
      // Resolve the base width. When the screen has no `base_screen_resolution`
      // set, `baseWidth` arrives undefined; every comparison below would then be
      // false (`1440 > undefined` etc.) and the ENTIRE breakpoint would be
      // dropped — silently discarding whatever was configured on that view
      // (geometry AND visibility). With no explicit base, treat the top-level
      // style as the widest/default layer and every breakpoint as a max-width
      // override that applies AT-AND-BELOW its own width. Defaulting the base to
      // +Infinity makes e.g. laptopL(1440) emit as `@media (max-width: 1440px)`:
      // it applies at ≤1440 (the device viewport) while the base style still
      // governs wider viewports. Defaulting to 1440 instead made the breakpoint
      // unwrapped (always-on) and leaked it ABOVE 1440, overriding the base's
      // hidden/large-screen styling.
      const effectiveBaseWidth = baseWidth ?? Number.POSITIVE_INFINITY;
      if (baseWidthForBreakpoint === effectiveBaseWidth) {
        // Breakpoint width equals an EXPLICIT base width: these ARE the base
        // styles, so emit them unwrapped (no @media). Previously this case matched
        // neither `>` nor `<` and produced NO CSS — the element rendered
        // class-less/invisible at the device viewport. (Unreachable when base is
        // the +Infinity default, by design — those go to the max-width branch.)
        Object.assign(componentStyles, generatedStyles);
      } else if (baseWidthForBreakpoint > effectiveBaseWidth) {
        componentStyles[`@media (min-width: ${baseWidthForBreakpoint}px)`] = generatedStyles;
      } else if (baseWidthForBreakpoint < effectiveBaseWidth) {
        componentStyles[`@media (max-width: ${baseWidthForBreakpoint}px)`] = generatedStyles;
      }
    });
  }

  if (states && !skipStates) {
    Object.keys(states).filter(st => !blockList.includes(st)).forEach((state) => {
      const modifiedState = state === 'focus-within'
        ? 'focusWithin'
        : state === 'click'
          ? 'active'
          : state;
      // `click`→`:active` is emitted with a doubled `&&` parent selector so its
      // specificity (`.cls.cls:active`) strictly exceeds `&:hover`/`&:focus`
      // (`.cls:hover`). This gives `click` an EXPLICIT priority over `hover`
      // that does NOT depend on the order `states` keys are emitted / the
      // browser cascade: while the pointer is held down over a hovered
      // component, the click style wins over hover (TNBEWEB-1970, UC-1).
      // Releasing the pointer drops `:active` and the component falls back to
      // `:hover`; with no `click` state nothing changes (UC-4, no regression).
      const ampersand = state === 'click' ? '&&' : '&';
      const stateStyles = {
        ...generateComponentStyles(states[state] as Type, undefined, true, display, undefined, parent_flex_direction),
      };
      // TNBEWEB-1980: a bare CSS `:hover` rule fires on a finger tap (and sticks
      // until the next outclick) on touch devices. Gate the `hover` state behind
      // `@media (hover: hover)` so it only applies where a hover-capable pointer
      // exists. Other states (focus/active/click) are unaffected — `click`→`:active`
      // keeps its doubled-`&&` specificity priority over hover (TNBEWEB-1970).
      if (state === 'hover') {
        componentStyles['@media (hover: hover)'] = {
          ...(componentStyles['@media (hover: hover)'] || {}),
          [`${ampersand}:${modifiedState}`]: stateStyles,
        };
      } else {
        componentStyles[`${ampersand}:${modifiedState}`] = stateStyles;
      }
    });
  }

  if (placeholder) {
    componentStyles['&::placeholder'] = placeholderStyles;
  }

  return componentStyles;
};

export default generateComponentStyles;
